import { Spinner, Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import { ImageRegular, VideoRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useResourceThumbnail, ThumbnailGeneratingError } from '../api/useResourceUrl';
import { sharableLinkDownloadUrl } from '../utils';
import { useResourceScheme, useSharableLink } from './ResourceSchemeContext';

const useStyles = makeStyles({
  container: {
    position: 'relative',
    width: '140px',
    height: '140px',
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  placeholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
    textAlign: 'center',
    padding: tokens.spacingHorizontalXS,
  },
  placeholderIcon: {
    fontSize: '24px',
    color: tokens.colorNeutralForeground3,
  },
});

interface ResourceThumbnailProps {
  domainId: number;
  fileId: string;
  mediaType?: 'image' | 'video';
}

export default function ResourceThumbnail({ domainId, fileId, mediaType }: ResourceThumbnailProps) {
  const styles = useStyles();
  const { t } = useTranslation();

  const scheme = useResourceScheme(fileId);
  const isApiResource = scheme.startsWith('xm');
  const sharableLink = useSharableLink(fileId);

  const { data: url, isLoading, error, isFetching } = useResourceThumbnail(
    domainId,
    isApiResource ? fileId : undefined,
  );

  const isGenerating = error instanceof ThumbnailGeneratingError && isFetching;
  const isError = !!error && !isGenerating;

  const altText = mediaType === 'video' ? t('recordings') : t('images');

  let content;
  if (scheme === 'db') {
    content = <Spinner size="small" aria-label={t('loading')} />;
  } else if (!isApiResource && sharableLink && mediaType !== 'video') {
    content = <img className={styles.image} src={sharableLinkDownloadUrl(sharableLink)} alt={altText} />;
  } else if (!isApiResource) {
    const PlaceholderIcon = mediaType === 'video' ? VideoRegular : ImageRegular;
    content = (
      <Tooltip content={t('thumbnailNotAvailable')} relationship="description">
        <div className={styles.placeholder} role="img" aria-label={altText}>
          <PlaceholderIcon className={styles.placeholderIcon} aria-hidden="true" />
        </div>
      </Tooltip>
    );
  } else if (isLoading || isGenerating) {
    content = <Spinner size="small" aria-label={t('loading')} />;
  } else if (url) {
    content = <img className={styles.image} src={url} alt={altText} />;
  } else if (isError) {
    content = (
      <div className={styles.placeholder} role="img" aria-label={altText}>
        <ImageRegular className={styles.placeholderIcon} aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className={styles.container} role="presentation">
      {content}
    </div>
  );
}
